import { CollateralFormDataValues } from './schema';
import { useMemo } from 'react';
import {
  real_estate_hooks,
  useLoanApplication,
} from '@axo/shared/data-access/hooks';

type DeepExhaustive<T> = {
  [K in keyof Required<T>]: T[K] extends object ? DeepExhaustive<T[K]> : T[K];
};

export const useCollateralFormDefaultValues = (
  applicationID: string,
): DeepExhaustive<CollateralFormDataValues> => {
  const { data: applicationData } = useLoanApplication(applicationID);
  const { data: latestMainApplicantRealEstateData } =
    real_estate_hooks.useFindRealEstates({
      personID: applicationData?.PersonID ?? undefined,
    });
  const { data: latestCoApplicantRealEstateData } =
    real_estate_hooks.useFindRealEstates({
      personID: applicationData?.CoApplicantPersonID ?? undefined,
    });

  const { data: applicationCollateralData } =
    real_estate_hooks.useGetApplicationCollateral({
      applicationID,
    });

  // Fetch estates that were added as collateral in the past but are no longer
  // returned by latest data for some reason
  const { data: addedRealEstateData } = real_estate_hooks.useFindRealEstates({
    personID: applicationData?.PersonID ?? undefined,
    includeAll: false,
    ids:
      applicationCollateralData
        ?.filter(
          (collateral) =>
            collateral.RealEstateID &&
            !latestMainApplicantRealEstateData?.find(
              (latestEstate) => latestEstate.ID === collateral.RealEstateID,
            ) &&
            !latestCoApplicantRealEstateData?.find(
              (latestEstate) => latestEstate.ID === collateral.RealEstateID,
            ),
        )
        .map((collateral) => collateral.ID) ?? [],
  });

  return useMemo(
    () => ({
      estates: Object.fromEntries(
        [
          ...(latestMainApplicantRealEstateData ?? []),
          ...(latestCoApplicantRealEstateData?.filter(
            (estate) =>
              !latestMainApplicantRealEstateData?.find(
                (latestEstate) => latestEstate.ID === estate.ID,
              ),
          ) ?? []),
          ...(addedRealEstateData ?? []),
        ].map(({ ID, ...estate }) => [
          ID,
          {
            PersonID: estate.PersonID,
            CollateralID: applicationCollateralData?.find(
              (collateral) => collateral.RealEstateID === ID,
            )?.ID,
            EstimatedValueReferenceNumber:
              estate.EstimatedValueReferenceNumber ?? undefined,

            IsInsured: estate.IsInsured ?? undefined,
            InsuranceCompany: estate.InsuranceCompany ?? undefined,

            MarketScoreArea: estate.MarketScoreArea ?? undefined,
            MarketScoreMunicipality:
              estate.MarketScoreMunicipality ?? undefined,

            OwnershipType: estate.OwnershipType ?? undefined,
            RealEstateType: estate.RealEstateType ?? undefined,

            CadastreBNumber: estate.CadastreBNumber ?? undefined,
            CadastreFNumber: estate.CadastreFNumber ?? undefined,
            CadastreGNumber: estate.CadastreGNumber ?? undefined,
            CadastreKNumber: estate.CadastreKNumber ?? undefined,
            CadastreSNumber: estate.CadastreSNumber ?? undefined,

            PrimaryLivingArea: estate.PrimaryLivingArea ?? undefined,

            StreetName: estate.StreetName ?? undefined,
            StreetNumber: estate.StreetNumber ?? undefined,
            StreetLetter: estate.StreetLetter ?? undefined,
            Postcode: estate.Postcode ?? undefined,
            Municipality: estate.Municipality ?? undefined,

            TotalMarketValueEstimate:
              estate.TotalMarketValueEstimate ?? undefined,
            TotalMarketValueEstimateApproved:
              estate.TotalMarketValueEstimateApproved ?? undefined,

            CooperativeApartmentCommonDebt:
              estate.CooperativeApartmentCommonDebt ?? undefined,
            CooperativeApartmentOrganizationNumber:
              estate.CooperativeApartmentOrganizationNumber ?? undefined,
            CooperativeApartmentShareNumber:
              estate.CooperativeApartmentShareNumber ?? undefined,

            Source: estate.Source ?? undefined,
            FetchID: estate.FetchID ?? undefined,
          },
        ]),
      ),
      isCollateral: Object.fromEntries(
        applicationCollateralData?.map((collateral) => [
          collateral.RealEstateID,
          true,
        ]) ?? [],
      ),
    }),
    [
      latestMainApplicantRealEstateData,
      latestCoApplicantRealEstateData,
      applicationCollateralData,
      addedRealEstateData,
    ],
  );
};
